import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, FileSpreadsheet, MessageSquare, Send } from "lucide-react";
import { api, API, apiError, getToken } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ABtn, AInput, ALabel, Panel } from "./AdminLayout";
import { useAuth } from "@/context/AuthContext";

const EXPORTS = [
  { id: "ventes", label: "Ventes & commandes", hint: "Commandes + lignes de vente avec marge", roles: ["admin"] },
  { id: "stock", label: "Stock / inventaire", hint: "Stock, réservé, vendu, seuils et valeurs", roles: ["admin", "warehouse"] },
  { id: "commissions", label: "Commissions vendeurs", hint: "Commissions par ligne + paiements versés", roles: ["admin"] },
  { id: "mouvements", label: "Mouvements de stock", hint: "Entrées, sorties, retours, ajustements", roles: ["admin", "warehouse"] },
];

export function AdminExports() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await fetch(`${API}/admin/exports/${kind}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Export impossible");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `empire-shop-${kind}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Fichier Excel téléchargé");
    } catch (e) {
      toast.error(e.message || "Export impossible");
    } finally {
      setBusy("");
    }
  };

  const available = EXPORTS.filter((e) => e.roles.includes(user?.role));

  return (
    <Panel
      title="Exports Excel"
      subtitle="Fichiers .xlsx prêts pour votre comptabilité"
      testid="admin-exports-panel"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {available.map((e) => (
          <div key={e.id} className="flex items-start gap-3 rounded-xl border border-slate-800 bg-slate-950 p-4" data-testid={`export-card-${e.id}`}>
            <FileSpreadsheet className="mt-0.5 h-8 w-8 shrink-0 text-emerald-400" />
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-bold text-white">{e.label}</p>
              <p className="mt-0.5 text-[11px] text-slate-400">{e.hint}</p>
              <ABtn onClick={() => download(e.id)} disabled={busy === e.id} className="mt-2" data-testid={`export-button-${e.id}`}>
                {busy === e.id ? "Préparation…" : "Télécharger .xlsx"}
              </ABtn>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function AdminSms() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [outbox, setOutbox] = useState({ items: [], pending: 0, sent: 0 });

  const load = async () => {
    try {
      const [s, o] = await Promise.all([api.get("/admin/sms-settings"), api.get("/admin/sms-outbox")]);
      setSettings(s.data);
      setDraft({
        provider: s.data.provider,
        sender_name: s.data.sender_name,
        api_url: s.data.api_url,
        api_key: "",
        api_secret: "",
        enabled: s.data.enabled,
        auto_send: s.data.auto_send,
      });
      setOutbox(o.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/sms-settings", draft);
      toast.success("Paramètres SMS enregistrés");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const markSent = async (sms) => {
    try {
      await api.post(`/admin/sms-outbox/${sms.id}/sent`);
      toast.success("Message marqué comme envoyé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!settings || !draft) return <p className="text-sm text-slate-400">Chargement…</p>;

  return (
    <div className="space-y-4" data-testid="admin-sms-page">
      <Panel
        title="Alertes SMS clients"
        subtitle="Fournisseur (agrégateur guinéen) et clés d'envoi"
        actions={<ABtn onClick={save} data-testid="sms-save">Enregistrer</ABtn>}
        testid="sms-settings-panel"
      >
        <p className="rounded-xl border border-amber-900/60 bg-amber-950/30 p-3 text-xs text-amber-200" data-testid="sms-note">
          {settings.note}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div>
            <ALabel>Fournisseur / agrégateur</ALabel>
            <AInput value={draft.provider} onChange={(e) => setDraft({ ...draft, provider: e.target.value })} placeholder="Nom de votre agrégateur SMS" data-testid="sms-provider" />
          </div>
          <div>
            <ALabel>Nom d'expéditeur affiché</ALabel>
            <AInput value={draft.sender_name} onChange={(e) => setDraft({ ...draft, sender_name: e.target.value })} data-testid="sms-sender" />
          </div>
          <div className="sm:col-span-2">
            <ALabel>URL de l'API d'envoi</ALabel>
            <AInput value={draft.api_url} onChange={(e) => setDraft({ ...draft, api_url: e.target.value })} placeholder="https://api.monagregateur.gn/sms/send" data-testid="sms-api-url" />
          </div>
          <div>
            <ALabel>Clé API {settings.api_key_masked && `(actuelle : ${settings.api_key_masked})`}</ALabel>
            <AInput type="password" value={draft.api_key} onChange={(e) => setDraft({ ...draft, api_key: e.target.value })} placeholder="laisser vide pour conserver" data-testid="sms-api-key" />
          </div>
          <div>
            <ALabel>Secret API {settings.api_secret_masked && `(actuel : ${settings.api_secret_masked})`}</ALabel>
            <AInput type="password" value={draft.api_secret} onChange={(e) => setDraft({ ...draft, api_secret: e.target.value })} placeholder="laisser vide pour conserver" data-testid="sms-api-secret" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} data-testid="sms-enabled" />
            Fournisseur configuré et actif
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={draft.auto_send} onChange={(e) => setDraft({ ...draft, auto_send: e.target.checked })} data-testid="sms-auto-send" />
            Envoi automatique (dès que l'API est branchée)
          </label>
        </div>
      </Panel>

      <Panel
        title="File d'attente des messages"
        subtitle={`${outbox.pending} en attente · ${outbox.sent} marqué(s) envoyé(s)`}
        testid="sms-outbox-panel"
      >
        <div className="space-y-2">
          {outbox.items.length === 0 && <p className="text-sm text-slate-500">Aucun message préparé pour le moment.</p>}
          {outbox.items.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-800 p-3" data-testid={`sms-row-${s.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-gold" />
                  <span className="font-num text-sm font-semibold text-white">{s.phone}</span>
                  <span className="text-[11px] text-slate-400">{s.recipient_name} · {s.order_number}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`ds-chip ${s.status === "envoyé" ? "border-emerald-700 bg-emerald-950 text-emerald-300" : "border-amber-700 bg-amber-950 text-amber-300"}`}>
                    {s.status}
                  </span>
                  <span className="text-[11px] text-slate-500">{fmtDate(s.created_at)}</span>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-300">{s.message}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <ABtn
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard?.writeText(s.message);
                    toast.success("Message copié");
                  }}
                  data-testid={`sms-copy-${s.id}`}
                >
                  <Copy className="h-3.5 w-3.5" /> Copier
                </ABtn>
                <a
                  href={`https://wa.me/${String(s.phone).replace(/[^0-9]/g, "")}?text=${encodeURIComponent(s.message)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#25D366] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#1da851]"
                  data-testid={`sms-whatsapp-${s.id}`}
                >
                  <Send className="h-3.5 w-3.5" /> Envoyer via WhatsApp
                </a>
                {s.status !== "envoyé" && (
                  <ABtn onClick={() => markSent(s)} data-testid={`sms-mark-sent-${s.id}`}>
                    <Check className="h-3.5 w-3.5" /> Marquer envoyé
                  </ABtn>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
